import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Field, useFieldContext } from './Field';

export type ControlSize = 'sm' | 'md';

/** Shared look for Input / Select / Textarea. */
export function controlClasses({ invalid, size = 'md', className }: { invalid?: boolean; size?: ControlSize; className?: string }): string {
  return cn(
    'block w-full rounded-md border bg-white text-gray-900 placeholder-gray-400 shadow-sm',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
    'dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500',
    'disabled:opacity-60 disabled:bg-gray-50 dark:disabled:bg-gray-800',
    invalid ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600',
    size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm',
    className,
  );
}

/** Props that make a control wrap itself in a Field (`required` stays a native attribute and is mirrored on the label). */
export interface FieldLikeProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Class for the wrapping Field (only used when `label` is given). */
  fieldClassName?: string;
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, FieldLikeProps {
  size?: ControlSize;
}

const BareInput = forwardRef<HTMLInputElement, Omit<InputProps, keyof FieldLikeProps>>(function BareInput(
  { size, className, id, 'aria-describedby': describedBy, 'aria-invalid': invalidProp, ...rest },
  ref,
) {
  const field = useFieldContext();
  const invalid = invalidProp !== undefined ? Boolean(invalidProp) : Boolean(field?.invalid);
  return (
    <input
      ref={ref}
      id={id ?? field?.id}
      aria-describedby={describedBy ?? field?.describedBy}
      aria-invalid={invalid || undefined}
      className={controlClasses({ invalid, size, className })}
      {...rest}
    />
  );
});

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, hint, error, fieldClassName, ...rest }, ref) {
  if (label === undefined) return <BareInput ref={ref} {...rest} />;
  return (
    <Field label={label} hint={hint} error={error} required={rest.required} id={rest.id} className={fieldClassName}>
      <BareInput ref={ref} {...rest} />
    </Field>
  );
});
